/**
 * AIPanel Component
 * Right-docked, resizable "Ask KCi & Team" panel — a flex sibling of the
 * main body content (mirrors UtilityPanel's dock/resize behavior) rather
 * than an overlay. Hosts the real Clara AI Angular module, loaded at
 * runtime as a Native Federation remote via RemotePage — no chat UI is
 * built here, the Angular module owns its own experience.
 */

import React, { useCallback, useRef, useState, useEffect } from 'react';
import { RemotePage } from '../../components/RemotePage/RemotePage';

interface AIPanelProps {
  isOpen: boolean;
  onClose: () => void;
  collapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
  onOpenInNewTab?: (sessionId: string | null) => void;
  sessionIdToLoad?: string | null;
  onWidthChange?: (width: number) => void;
  disclaimerText?: string;
}

const AI_SPARK_SVG = (
  <svg width="20" height="20" viewBox="6 6 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M16.0098 6.00098C16.0098 11.5202 20.4812 15.9944 25.999 16V16.0098C20.4846 16.0154 16.0154 20.4846 16.0098 25.999H16.001C15.9953 20.4812 11.5202 16.0098 6.00098 16.0098H6V16C11.5227 16 16.0009 11.5236 16.001 6.00098V6H16.0098V6.00098Z"
      fill="currentColor"
    />
  </svg>
);

export const PANEL_MIN_WIDTH = 320;
export const PANEL_MAX_WIDTH = 720;
export const PANEL_DEFAULT_WIDTH = 448;
export const PANEL_COLLAPSED_WIDTH = 56;

export const AIPanel = React.memo(function AIPanel({ 
  isOpen, 
  onClose, 
  collapsed = false,
  onCollapsedChange,
  onOpenInNewTab,
  sessionIdToLoad,
  onWidthChange
}: AIPanelProps) {
  const [width, setWidth] = useState<number>(PANEL_DEFAULT_WIDTH);
  const dragStartRef = useRef<{ x: number; startWidth: number } | null>(null);

  const claraRemoteEntryUrl = import.meta.env.VITE_claraAppUrl
    ? `${import.meta.env.VITE_claraAppUrl}/remoteEntry.json`
    : undefined;

  // The "logical" width of the panel's content right now — full width when
  // expanded, the narrow icon-strip width when collapsed. .ai-panel__card is
  // pinned to this (not always `width`) so collapsing genuinely shrinks the
  // content instead of just clipping a full-width card.
  const contentWidth = collapsed ? PANEL_COLLAPSED_WIDTH : width;

  // Notify parent when width changes
  useEffect(() => {
    onWidthChange?.(contentWidth);
  }, [contentWidth, onWidthChange]);

  useEffect(() => {
    (window as unknown as Record<string, unknown>).__CLARA_AIPANEL_COLLAPSED__ = collapsed;

    return () => {
      (window as unknown as Record<string, unknown>).__CLARA_AIPANEL_COLLAPSED__ = false;
    };
  }, [collapsed]);

  const handleResizeStart = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      dragStartRef.current = { x: e.clientX, startWidth: width };

      const onMove = (ev: MouseEvent) => {
        const drag = dragStartRef.current;
        if (!drag) return;
        // Right-edge panel — dragging LEFT increases width.
        const dx = drag.x - ev.clientX;
        const next = Math.max(
          PANEL_MIN_WIDTH,
          Math.min(PANEL_MAX_WIDTH, drag.startWidth + dx)
        );
        setWidth(next);
      };

      const onUp = () => {
        dragStartRef.current = null;
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
      };

      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    },
    [width]
  );

  const toggleCollapsed = useCallback(() => {
    onCollapsedChange?.(!collapsed);
  }, [collapsed, onCollapsedChange]);

  const handleOpenInNewTab = useCallback(() => {
    const requestId = `clara-session-${Date.now()}`;
    let completed = false;
    const timeoutIdRef: { current: number | undefined } = { current: undefined };

    const openTab = (sessionId: string | null) => {
      if (completed) return;
      completed = true;
      if (timeoutIdRef.current !== undefined) {
        window.clearTimeout(timeoutIdRef.current);
      }
      window.removeEventListener('kci:active-chat-session', handleSession);
      onOpenInNewTab?.(sessionId);
    };

    const handleSession = (event: Event) => {
      const detail = (event as CustomEvent<{ requestId?: string; sessionId?: string | null }>).detail;
      if (detail?.requestId === requestId) openTab(detail.sessionId ?? null);
    };

    window.addEventListener('kci:active-chat-session', handleSession);
    window.dispatchEvent(new CustomEvent('kci:request-active-chat-session', { detail: { requestId } }));
    timeoutIdRef.current = window.setTimeout(() => openTab(sessionIdToLoad ?? null), 300);
  }, [onOpenInNewTab, sessionIdToLoad]);

  return (
    <aside
      className={'ai-panel' + (isOpen ? ' ai-panel--open' : '') + (collapsed ? ' ai-panel--collapsed' : '')}
      style={{ width: isOpen ? `${contentWidth}px` : 0 }}
      role="complementary"
      aria-label="Ask KCi"
      aria-hidden={!isOpen}
    >
      {isOpen && !collapsed && (
        <div
          className="ai-panel__resize-handle"
          onMouseDown={handleResizeStart}
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize panel"
        />
      )}
      {/* Pinned to contentWidth (not the parent's animated open/close width)
          so open/close clips via .ai-panel's overflow:hidden instead of
          reflowing, while collapse/expand transitions this width in sync
          with the parent for a clean strip-resize instead of a clip. */}
      <div className="ai-panel__card" style={{ width: `${contentWidth}px` }}>
        <div className="ai-panel__header">
          <div className="ai-panel__header-left">
            {collapsed && (
              <span className="ai-panel__spark-icon" style={{ display: 'flex', alignItems: 'center' }}>
                {AI_SPARK_SVG}
              </span>
            )}
            {!collapsed && <h3 className="ai-panel__title">Ask KCi</h3>}
          </div>
          <div className="ai-panel__header-actions">
            {!collapsed && (
              <button
                className="icon-btn icon-btn--sm"
                onClick={handleOpenInNewTab}
                type="button"
                aria-label="Fullscreen"
                tabIndex={isOpen ? 0 : -1}
              >
                <span className="material-symbols-outlined">
                  fullscreen
                </span>
              </button>
            )}
            {collapsed && (
              <button
                className="icon-btn icon-btn--sm"
                onClick={toggleCollapsed}
                type="button"
                aria-label="Expand panel"
                aria-expanded={false}
                tabIndex={isOpen ? 0 : -1}
              >
                <span className="material-symbols-outlined">chevron_left</span>
              </button>
            )}
            {!collapsed && (
              <button
                className="icon-btn icon-btn--sm"
                onClick={onClose}
                type="button"
                aria-label="Close AI panel"
                tabIndex={isOpen ? 0 : -1}
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            )}
          </div>
        </div>
        <div className="ai-panel__body">
          {isOpen && (
            <RemotePage
              remoteEntryUrl={claraRemoteEntryUrl}
              remoteName="Clara AI"
              remoteRuntimeName="ai-chat"
              remotePort={4100}
              className="ai-panel__remote"
              sessionIdToLoad={sessionIdToLoad}
              standalone
            />
          )}
        </div>
      </div>
    </aside>
  );
});

export default AIPanel;
